import { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Waves } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { FLOW_VERDICT_TONE } from '../flow/FlowEvaluationCard';
import { getLatestFlowEvaluation, latestFlowEvaluationKey } from '../../services/flowSurveyService';

export interface FlowInsightSectionProps {
  aquariumId: string | undefined;
}

/**
 * Flow part of the Reef Intelligence card: the newest AI flow evaluation
 * (verdict, summary, the most important recommendation) with a link to its flow
 * map. Like the lighting part, nothing is shown before a survey was evaluated.
 */
export function FlowInsightSection({ aquariumId }: FlowInsightSectionProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const headingId = useId();
  const query = useQuery({
    queryKey: latestFlowEvaluationKey(aquariumId ?? ''),
    queryFn: () => getLatestFlowEvaluation(aquariumId!),
    enabled: Boolean(aquariumId),
  });

  if (!aquariumId || query.isPending || query.isError) return null;
  const evaluation = query.data;
  if (!evaluation) return null;
  const { result } = evaluation;
  const top = [...result.recommendations].sort((a, b) => ['high', 'medium', 'low'].indexOf(a.priority) - ['high', 'medium', 'low'].indexOf(b.priority))[0];
  const measured = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(evaluation.surveyMeasuredAt));

  return (
    <Stack gap="3" as="section" aria-labelledby={headingId} data-testid="reef-insight-flow">
      <Inline gap="2" align="center">
        <Waves size={18} aria-hidden="true" />
        <Heading level={4} id={headingId}>{t('insights.flow.title')}</Heading>
        <Badge tone={FLOW_VERDICT_TONE[result.verdict]}>{t(`flow.evaluation.verdict.${result.verdict}`)}</Badge>
      </Inline>
      <Text lang={evaluation.language}>{result.summary}</Text>
      {top && <Text size="sm" lang={evaluation.language}>{t('insights.flow.topRecommendation', { text: top.text })}</Text>}
      <Text size="sm" color="muted">{t('insights.flow.basedOn', { title: evaluation.surveyTitle, date: measured })}</Text>
      {evaluation.outdated && <Alert tone="info" compact live="off">{t('insights.flow.outdated')}</Alert>}
      <Inline gap="2">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/aquariums/${encodeURIComponent(aquariumId)}/flow/${encodeURIComponent(evaluation.surveyId)}`)}>
          {t('insights.flow.open')}
        </Button>
      </Inline>
    </Stack>
  );
}

export default FlowInsightSection;
